/**
 * As notificações internas em frases (`R-08`, ADR-078). Só **conta** o que a API decidiu: o
 * canal de cada destinatário, a situação, se pode reenviar ou dar ciente — tudo isso vem do
 * servidor, que o tira de `transit_domain.notificacoes`.
 */
import type { NotificacaoInterna } from "../api/tipos";
import type { Significado } from "../componentes/tinta";

export type Caixa = "equipe" | "sistemas" | "serpro";

export const CAIXAS: { chave: Caixa; rotulo: string; explicacao: string }[] = [
  {
    chave: "equipe",
    rotulo: "Equipe",
    explicacao:
      "O que o Backoffice e a Monitoria CDT recebem aqui, na tela: cada uma espera alguém dar ciente.",
  },
  {
    chave: "sistemas",
    rotulo: "Sistemas",
    explicacao:
      "O que vai à Sugestão de Autuação e à operadora da TAG por webhook assinado. O cockpit insiste por até 24 horas, com a espera dobrando até uma hora; depois, alguém reenvia.",
  },
  {
    chave: "serpro",
    rotulo: "CNH Digital",
    explicacao:
      "Os avisos do 8º e do 15º dia ao condutor: quem manda é a SERPRO, pela CNH Digital. O cockpit só registra.",
  },
];

/** A cor do badge da situação. O que espera alguém é âmbar; o que não chegou, vermelho. */
export const COR_DA_SITUACAO: Record<string, Significado> = {
  PENDENTE: "amarelo",
  ENTREGUE: "verde",
  FALHOU: "vermelho",
  SEM_DESTINO: "vermelho",
  NA_TELA: "azul",
  CIENTE: "verde",
  PELA_SERPRO: "cinza",
  HISTORICO: "cinza",
};

export function corDaSituacao(situacao: string): Significado {
  return COR_DA_SITUACAO[situacao] ?? "cinza";
}

const plural = (n: number, um: string, varios: string) =>
  `${n} ${n === 1 ? um : varios}`;

/** A linha da entrega, para o que foi a um sistema: quantas vezes, o que respondeu, e o
 *  que acontece agora. */
export function linhaDaEntrega(
  n: Pick<
    NotificacaoInterna,
    | "situacao"
    | "tentativas"
    | "proxima_tentativa_em"
    | "recibo"
    | "simulado"
    | "motivo"
  >,
  hora: (iso: string) => string,
): string {
  const vezes = plural(n.tentativas, "tentativa", "tentativas");
  switch (n.situacao) {
    case "ENTREGUE":
      return `Entregue na ${n.tentativas}ª tentativa${n.recibo ? `, recibo ${n.recibo}` : ""}${
        n.simulado ? " — quem recebeu foi o emulador da demonstração" : ""
      }.`;
    case "PENDENTE":
      return n.tentativas === 0
        ? "Sai na próxima rodada do cockpit."
        : `${vezes} sem sucesso. A próxima ${
            n.proxima_tentativa_em
              ? `às ${hora(n.proxima_tentativa_em)}`
              : "em breve"
          }.`;
    case "FALHOU":
      return `Não chegou (${vezes}). Reenvie depois de resolver o motivo.`;
    case "SEM_DESTINO":
      return "Não há endereço cadastrado para este sistema: o administrador cadastra em Administração → Notificações aos sistemas, e a supervisão reenvia.";
    default:
      return "";
  }
}

/** O título do contador do cabeçalho da SENATRAN: o que espera alguém, e nada quando não há. */
export function oQueEspera(contagem: {
  equipe_sem_ciente: number;
  sistemas_nao_entregues: number;
  sistemas_sem_destino: number;
}): number {
  return (
    contagem.equipe_sem_ciente +
    contagem.sistemas_nao_entregues +
    contagem.sistemas_sem_destino
  );
}
